import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Landmark, Calculator, Info, Building2, CreditCard,
  ChevronRight, Save, RefreshCw, AlertCircle, ChevronDown, ChevronUp,
  HelpCircle, Lightbulb, BookOpen, TrendingUp, DollarSign, Minus, Plus, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/config';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

/* ─── Composant boîte d'info expandable ─── */
function InfoBox({ title, icon: Icon = Lightbulb, color = "blue", children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const colors = {
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    purple: "bg-purple-50 border-purple-200 text-purple-800",
    gray: "bg-slate-50 border-slate-200 text-slate-700",
  };
  const btnColors = {
    blue: "hover:bg-blue-100 text-blue-700",
    amber: "hover:bg-amber-100 text-amber-700",
    emerald: "hover:bg-emerald-100 text-emerald-700",
    purple: "hover:bg-purple-100 text-purple-700",
    gray: "hover:bg-slate-100 text-slate-600",
  };
  return (
    <div className={`rounded-lg border ${colors[color]} overflow-hidden`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm font-medium transition-colors ${btnColors[color]}`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className="flex-1">{title}</span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 text-sm space-y-2 border-t border-current border-opacity-20">
          {children}
        </div>
      )}
    </div>
  );
}

/* ─── Bulle d'info inline ─── */
function Tooltip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block">
      <HelpCircle
        className="w-3.5 h-3.5 text-gray-400 cursor-help inline ml-1"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      />
      {show && (
        <span className="absolute z-50 bottom-5 left-0 w-64 bg-gray-900 text-white text-xs rounded-lg p-2 shadow-xl leading-relaxed">
          {text}
        </span>
      )}
    </span>
  );
}

/* ─── Ligne du calcul ─── */
function LigneBilan({ label, value, color = "gray", sign = "", tooltip, bold = false }) {
  const textColor = color === "green" ? "text-green-700" : color === "red" ? "text-red-600" : color === "blue" ? "text-blue-600" : color === "emerald" ? "text-emerald-700" : "text-gray-800";
  return (
    <div className={`flex justify-between items-center py-1.5 ${bold ? 'font-bold text-base' : 'text-sm'}`}>
      <span className="text-gray-600 flex items-center gap-1">
        {label}
        {tooltip && <Tooltip text={tooltip} />}
      </span>
      <span className={`font-semibold ${textColor}`}>{sign}{formatCurrency(Math.abs(value))}</span>
    </div>
  );
}

const MULTIPLES_GUIDE = [
  { range: "1x – 2x", label: "Très petite entreprise (< 500 000$ CA)", description: "Le propriétaire est irremplaçable. Sans lui, l'entreprise n'existe pas. Peu ou pas de processus en place.", exemple: "Un plombier solo ou un électricien sans employés." },
  { range: "2x – 3x", label: "PME standard (500 000$ – 1M$ CA)", description: "Quelques employés de confiance, clientèle établie. Mais le propriétaire reste très présent au quotidien.", exemple: "Une petite équipe de peintres avec un propriétaire actif sur les chantiers." },
  { range: "3x – 4x", label: "PME solide (1M$ – 2M$ CA)", description: "Équipe stable, revenus récurrents, marque locale reconnue. Le propriétaire peut s'absenter quelques semaines.", exemple: "Une entreprise structurée avec chefs d'équipe autonomes et bonne réputation." },
  { range: "4x – 5x", label: "Entreprise structurée (2M$ – 5M$ CA)", description: "Processus documentés, logiciels de gestion, croissance régulière. Un acheteur peut reprendre sans dépendre du fondateur.", exemple: "Une franchise ou une entreprise de services avec systèmes en place." },
  { range: "5x – 7x", label: "Leader régional (5M$+ CA)", description: "Marque forte, barrières à l'entrée, systèmes robustes. L'entreprise fonctionne pratiquement sans le propriétaire.", exemple: "Un groupe de services avec plusieurs équipes et contrats à long terme." },
];

const DEFAULT_CONFIG = {
  multiple: 3.5,
  annee_reference: new Date().getFullYear() - 1,
  retraitement_dividendes: 0,
  retraitement_salaire_proprio: 0,
  retraitement_loyer_marche: 0,
  retraitement_one_time_pos: 0,
  retraitement_one_time_neg: 0,
  notes: "",
};

export function Revente() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [donneesAnnuelles, setDonneesAnnuelles] = useState(null);
  const [projection, setProjection] = useState(null);
  const [actifsDettes, setActifsDettes] = useState({ assets: [], debts: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [modeProjection, setModeProjection] = useState(false);

  const currentYear = new Date().getFullYear();

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cfgRes, actRes] = await Promise.all([
        axios.get(`${API_URL}/api/configuration/ebitda-config`),
        axios.get(`${API_URL}/api/configuration/actifs-dettes`),
      ]);
      if (cfgRes.status === 200) { const d = cfgRes.data; if (d.config) setConfig({ ...DEFAULT_CONFIG, ...d.config }); }
      if (actRes.status === 200) { const d = actRes.data; setActifsDettes(d); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadDonneesAnnuelles = useCallback(async (annee) => {
    try {
      const res = await axios.get(`${API_URL}/api/revente/donnees-annuelles/${annee}`);
      if (res.status === 200) {
        const data = res.data;
        setDonneesAnnuelles(data);
        // Charger la projection si c'est l'année en cours
        if (annee === currentYear) {
          const projRes = await axios.get(`${API_URL}/api/revente/projection/${annee}`);
          if (projRes.status === 200) {
            const projData = projRes.data;
            setProjection(projData);
            // Auto-activer la projection si pas assez de données réelles
            if (projData.has_objectifs && (data.revenus || 0) < 50000) {
              setModeProjection(true);
            }
          }
        } else {
          setProjection(null);
          setModeProjection(false);
        }
      }
    } catch (e) { console.error(e); }
  }, [currentYear]);

  useEffect(() => { if (config.annee_reference) loadDonneesAnnuelles(config.annee_reference); }, [config.annee_reference, loadDonneesAnnuelles]);

  const updateConfig = (field, value) => { setConfig(prev => ({ ...prev, [field]: value })); setHasChanges(true); };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/configuration/ebitda-config`, config);
      if (r.status === 200) { setHasChanges(false); toast.success("Configuration sauvegardée"); }
    } catch { toast.error("Erreur de sauvegarde"); }
    finally { setSaving(false); }
  };

  // Données utilisées (réelles OU projection)
  const donneesEffectives = modeProjection && projection
    ? { ...donneesAnnuelles, ebitda: projection.ebitda_projete, revenus: projection.ca_projete }
    : donneesAnnuelles;

  // Calculs
  const ebitdaBase = donneesEffectives?.ebitda || 0;
  const totalRetraitements = (parseFloat(config.retraitement_dividendes) || 0)
    + (parseFloat(config.retraitement_salaire_proprio) || 0)
    + (parseFloat(config.retraitement_loyer_marche) || 0)
    + (parseFloat(config.retraitement_one_time_neg) || 0)
    - (parseFloat(config.retraitement_one_time_pos) || 0);
  const adjustedEbitda = ebitdaBase + totalRetraitements;
  const multipleActuel = parseFloat(config.multiple) || 3.5;
  const valeurEntreprise = adjustedEbitda * multipleActuel;
  const now = new Date();
  const totalActifs = actifsDettes.assets.reduce((sum, a) => {
    const purchaseDate = new Date(a.purchaseDate);
    const monthsElapsed = (now.getFullYear() - purchaseDate.getFullYear()) * 12 + (now.getMonth() - purchaseDate.getMonth());
    const purchaseValue = parseFloat(a.purchaseValue) || 0;
    const residualValue = parseFloat(a.residualValue) || 0;
    const years = parseInt(a.depreciationYears) || 5;
    const monthlyDep = (purchaseValue - residualValue) / (years * 12);
    return sum + Math.max(residualValue, purchaseValue - (monthlyDep * Math.max(0, monthsElapsed)));
  }, 0);
  const totalDettes = actifsDettes.debts.reduce((sum, d) => sum + (parseFloat(d.currentBalance) || 0), 0);
  const valeurCapitauxPropres = valeurEntreprise - totalDettes;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
    </div>
  );

  return (
    <div data-testid="revente-page" className="space-y-6 max-w-7xl mx-auto">

      {/* ═══ HEADER ═══ */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Landmark className="w-7 h-7 text-emerald-600" />
            Valorisation & Revente
          </h1>
          <p className="text-gray-500 mt-1 text-sm">Estimez combien vaut votre entreprise si vous décidez de la vendre aujourd&apos;hui</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => loadDonneesAnnuelles(config.annee_reference)} size="sm">
            <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
          </Button>
          <Button variant="outline" onClick={() => window.print()} size="sm" className="no-print" data-testid="export-pdf-btn">
            <Printer className="w-4 h-4 mr-1" /> Exporter PDF
          </Button>
          <Button onClick={saveConfig} disabled={!hasChanges || saving} className="bg-emerald-600 hover:bg-emerald-700" size="sm">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Sauvegarde..." : "Sauvegarder"}
          </Button>
        </div>
      </div>

      {/* ═══ BANDEAU PROJECTION ═══ */}
      {projection && projection.has_objectifs && config.annee_reference === currentYear && (
        <div data-testid="projection-banner" className={`flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border ${modeProjection ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-center gap-2">
            <TrendingUp className={`w-4 h-4 shrink-0 ${modeProjection ? 'text-amber-600' : 'text-blue-600'}`} />
            <div>
              <p className={`text-sm font-semibold ${modeProjection ? 'text-amber-800' : 'text-blue-800'}`}>
                {modeProjection
                  ? `Valorisation projetée ${currentYear} — ${projection.mois_reels} mois réels + ${12 - projection.mois_reels} mois d'objectifs`
                  : `Mode projection disponible — basé sur vos objectifs de CA mensuels`}
              </p>
              {modeProjection && (
                <p className="text-xs text-amber-600">
                  CA projeté : {new Intl.NumberFormat('fr-CA', {style:'currency',currency:'CAD',maximumFractionDigits:0}).format(projection.ca_projete)} · Marge EBITDA ref ({projection.ref_year}) : {projection.marge_ebitda_ref}%
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => setModeProjection(m => !m)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${modeProjection ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            {modeProjection ? 'Afficher données réelles' : `Activer projection ${currentYear}`}
          </button>
        </div>
      )}

      {/* ═══ EXPLICATION GÉNÉRALE ═══ */}
      <InfoBox title="Comment fonctionne cette page ? Cliquez pour comprendre" icon={BookOpen} color="blue" defaultOpen={true}>
        <div className="space-y-3">
          <p className="font-medium text-blue-900">Cette page calcule la valeur de revente de votre entreprise en 3 étapes simples :</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <p className="font-bold text-blue-700 text-lg">Étape 1</p>
              <p className="font-medium mt-1">Calculer l&apos;EBITDA</p>
              <p className="text-gray-600 text-xs mt-1">C&apos;est le profit annuel de votre entreprise <strong>avant</strong> de payer les banques, le gouvernement et les amortissements. C&apos;est la vraie capacité de votre business à générer de l&apos;argent.</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <p className="font-bold text-blue-700 text-lg">Étape 2</p>
              <p className="font-medium mt-1">Appliquer un Multiple</p>
              <p className="text-gray-600 text-xs mt-1">Un acheteur paie généralement <strong>3 à 5 fois</strong> l&apos;EBITDA annuel. Plus votre entreprise est stable et indépendante du propriétaire, plus le multiple est élevé.</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <p className="font-bold text-blue-700 text-lg">Étape 3</p>
              <p className="font-medium mt-1">Soustraire les Dettes</p>
              <p className="text-gray-600 text-xs mt-1">La valeur d&apos;entreprise moins vos dettes actuelles = ce que <strong>vous</strong> empochez réellement à la vente.</p>
            </div>
          </div>
        </div>
      </InfoBox>

      {/* ═══ KPIs RÉSUMÉ ═══ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="col-span-2 lg:col-span-4">
          <InfoBox title="Que représentent ces 4 chiffres ?" icon={HelpCircle} color="gray">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
              <div><strong className="text-emerald-700">EBITDA de base :</strong> Le profit brut de votre entreprise cette année, calculé automatiquement depuis vos données QuickBooks.</div>
              <div><strong className="text-blue-700">EBITDA Ajusté :</strong> L&apos;EBITDA après corrections (dividendes, salaire proprio…) pour montrer la vraie performance à un acheteur potentiel.</div>
              <div><strong className="text-purple-700">Valeur d&apos;entreprise :</strong> Ce qu&apos;un acheteur paierait pour TOUT acheter (incluant vos dettes). C&apos;est EBITDA × Multiple.</div>
              <div><strong className="text-amber-700">Valeur actionnaire :</strong> Ce que <strong>vous</strong> empochez après avoir remboursé vos dettes avec l&apos;argent de la vente. C&apos;est votre chèque final.</div>
            </div>
          </InfoBox>
        </div>

        <Card className="border-emerald-200 bg-emerald-50/40 col-span-1">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">EBITDA de base</p>
            <p className="text-xl sm:text-2xl font-bold text-emerald-800 mt-1">{formatCurrency(ebitdaBase)}</p>
            <p className="text-xs text-gray-500 mt-1">Année {config.annee_reference} · {donneesAnnuelles?.marge_ebitda ?? 0}% de marge</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/40 col-span-1">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-blue-700 font-semibold uppercase tracking-wide">EBITDA Ajusté</p>
            <p className="text-xl sm:text-2xl font-bold text-blue-800 mt-1">{formatCurrency(adjustedEbitda)}</p>
            <p className="text-xs text-gray-500 mt-1">
              {totalRetraitements >= 0 ? '+' : ''}{formatCurrency(Math.abs(totalRetraitements))} de retraitements
            </p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50/40 col-span-1">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-purple-700 font-semibold uppercase tracking-wide">Valeur d&apos;entreprise</p>
            <p className="text-xl sm:text-2xl font-bold text-purple-800 mt-1">{formatCurrency(valeurEntreprise)}</p>
            <p className="text-xs text-gray-500 mt-1">EBITDA × {multipleActuel}×</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/40 col-span-1">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Votre chèque final</p>
            <p className={`text-xl sm:text-2xl font-bold mt-1 ${valeurCapitauxPropres >= 0 ? 'text-amber-800' : 'text-red-700'}`}>{formatCurrency(valeurCapitauxPropres)}</p>
            <p className="text-xs text-gray-500 mt-1">Après remboursement des dettes</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="calcul">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="calcul" className="flex-1 sm:flex-none"><Calculator className="w-4 h-4 mr-1 hidden sm:inline" /> Calcul EBITDA</TabsTrigger>
          <TabsTrigger value="bilan" className="flex-1 sm:flex-none"><Building2 className="w-4 h-4 mr-1 hidden sm:inline" /> Bilan</TabsTrigger>
          <TabsTrigger value="guide" className="flex-1 sm:flex-none"><Info className="w-4 h-4 mr-1 hidden sm:inline" /> Guide</TabsTrigger>
        </TabsList>

        {/* ════════════════════════════════════
            ONGLET CALCUL
        ════════════════════════════════════ */}
        <TabsContent value="calcul" className="space-y-5 mt-4">

          {/* SECTION 1 : EBITDA de base */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">1</span>
                Calcul de l&apos;EBITDA de base
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <InfoBox title="Qu'est-ce que l'EBITDA ?" icon={HelpCircle} color="emerald">
                <p><strong>EBITDA</strong> = <strong>E</strong>arnings <strong>B</strong>efore <strong>I</strong>nterest, <strong>T</strong>axes, <strong>D</strong>epreciation & <strong>A</strong>mortization</p>
                <p className="mt-2">En français : le profit de votre entreprise <strong>avant</strong> de payer :</p>
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li><strong>Les intérêts</strong> sur vos emprunts (banques)</li>
                  <li><strong>Les impôts</strong> (gouvernement)</li>
                  <li><strong>Les amortissements</strong> (dépréciation comptable de vos actifs)</li>
                </ul>
                <p className="mt-2 bg-white rounded p-2 border border-emerald-200">
                  <strong>Pourquoi c&apos;est important ?</strong> Un acheteur veut savoir combien l&apos;entreprise génère <em>intrinsèquement</em>, peu importe comment elle est financée ou structurée fiscalement. L&apos;EBITDA permet de comparer des entreprises sur une base équitable.
                </p>
              </InfoBox>

              <div>
                <Label>Année de référence utilisée pour le calcul</Label>
                <div className="flex items-center gap-3 mt-1">
                  <Input
                    type="number"
                    value={config.annee_reference}
                    onChange={(e) => updateConfig('annee_reference', parseInt(e.target.value))}
                    className="w-28"
                  />
                  <p className="text-xs text-gray-500">Idéalement l&apos;année complète la plus récente disponible dans QuickBooks.</p>
                </div>
              </div>

              {donneesAnnuelles ? (
                <div className="rounded-lg overflow-hidden border border-gray-200">
                  <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Compte de résultats {config.annee_reference} — calculé depuis vos données QuickBooks
                  </div>
                  <div className="p-4 space-y-1.5">
                    <LigneBilan label="Chiffre d'affaires (revenus)" value={donneesAnnuelles.revenus} color="green"
                      tooltip="Total de toutes vos factures encaissées dans l'année, hors taxes." />
                    <LigneBilan label="Coûts directs — main d'oeuvre & matériaux (Brut)" value={-donneesAnnuelles.depenses_brut} color="red" sign="− "
                      tooltip="Salaires des peintres, matériaux, sous-traitants. Ce sont les coûts qui varient directement avec le volume de travail." />
                    <div className="border-t my-1.5 pt-1.5">
                      <div className="flex justify-between text-sm font-semibold">
                        <span className="text-gray-700">= Profit brut</span>
                        <span className={donneesAnnuelles.profit_brut >= 0 ? 'text-emerald-700' : 'text-red-600'}>{formatCurrency(donneesAnnuelles.profit_brut)}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">Ce qui reste après avoir payé les travailleurs et les matériaux.</p>
                    </div>
                    <LigneBilan label="Frais d'exploitation — véhicules, outils, pub (Marge)" value={-donneesAnnuelles.depenses_marge} color="red" sign="− "
                      tooltip="Frais semi-variables: carburant, entretien véhicules, publicité, assurances chantier, etc." />
                    <LigneBilan label="Frais G&A — bureau, admin, direction (Net)" value={-donneesAnnuelles.depenses_net} color="red" sign="− "
                      tooltip="Frais fixes: loyer bureau, comptable, téléphones, logiciels, salaires administratifs, etc." />
                    <div className="border-t my-1.5 pt-1.5">
                      <div className="flex justify-between text-sm font-semibold">
                        <span className="text-gray-700">= Résultat net (avant impôts)</span>
                        <span className={donneesAnnuelles.profit_net >= 0 ? 'text-gray-800' : 'text-red-600'}>{formatCurrency(donneesAnnuelles.profit_net)}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">Ce que l&apos;entreprise a gagné après toutes ses dépenses opérationnelles.</p>
                    </div>
                    <div className="bg-blue-50 rounded p-2 mt-2 space-y-1">
                      <p className="text-xs font-semibold text-blue-700">On ajoute maintenant les éléments non-cash pour obtenir l&apos;EBITDA :</p>
                      <LigneBilan label="+ Impôts estimés (15%)" value={donneesAnnuelles.impots_estimes} color="blue" sign="+ "
                        tooltip="Estimation des impôts corporatifs à 15%. On les rajoute car un acheteur différent pourrait avoir une structure fiscale différente." />
                      <LigneBilan label="+ Intérêts annuels sur vos dettes" value={donneesAnnuelles.interets_annuels} color="blue" sign="+ "
                        tooltip="Frais d'intérêts annuels calculés sur vos 8 dettes actuelles. On les rajoute car un acheteur pourrait financer différemment." />
                      <LigneBilan label="+ Amortissements annuels de vos actifs" value={donneesAnnuelles.amortissement_annuel} color="blue" sign="+ "
                        tooltip="Dépréciation comptable de vos 32 actifs (véhicules, équipements…). Ce n'est pas une dépense cash réelle, donc on la rajoute." />
                    </div>
                    <div className="border-t-2 border-emerald-300 mt-2 pt-2 flex justify-between font-bold text-base">
                      <span className="text-emerald-700 flex items-center gap-1">
                        = EBITDA
                        <Tooltip text="Earnings Before Interest, Taxes, Depreciation & Amortization — la vraie capacité bénéficiaire de votre entreprise." />
                      </span>
                      <span className="text-emerald-700">{formatCurrency(ebitdaBase)}</span>
                    </div>
                    <p className="text-xs text-gray-400">Marge EBITDA : {donneesAnnuelles.marge_ebitda}% du chiffre d&apos;affaires</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-4 bg-amber-50 rounded-lg text-sm text-amber-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <p>Aucune donnée QuickBooks pour {config.annee_reference}. Importez vos données d&apos;abord.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SECTION 2 : Retraitements */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">2</span>
                Retraitements & Normalisations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <InfoBox title="Pourquoi « retraiter » l'EBITDA ? C'est quoi exactement ?" icon={HelpCircle} color="blue" defaultOpen={true}>
                <p>Votre EBITDA de base reflète <em>votre</em> façon de gérer l&apos;entreprise. Un acheteur veut voir ce que l&apos;entreprise vaut <strong>sans vous</strong>. Les retraitements éliminent les dépenses ou revenus liés à votre situation personnelle.</p>
                <p className="mt-2 font-medium">Exemple concret :</p>
                <p className="mt-1 bg-white rounded p-2 border border-blue-200 text-xs">Si vous vous versez 200 000$ en dividendes et qu&apos;un gérant embauché coûterait 120 000$, vous <strong>rajoutez</strong> vos dividendes à l&apos;EBITDA, puis un acheteur déduirait lui-même le salaire du futur gérant. Cela montre la vraie rentabilité de l&apos;entreprise.</p>
              </InfoBox>

              <div className="space-y-4">
                {/* Dividendes */}
                <div className="p-4 rounded-lg border border-gray-200 bg-gray-50/50 space-y-2">
                  <div className="flex items-start gap-2">
                    <Plus className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <Label className="text-sm font-semibold">Dividendes propriétaires à rajouter</Label>
                      <p className="text-xs text-gray-500 mt-0.5">Montant annuel de dividendes que vous vous êtes versés (ils ne passent pas comme charge dans l&apos;état des résultats).</p>
                      <div className="bg-white border border-dashed border-blue-200 rounded p-2 mt-2 text-xs text-blue-700">
                        <strong>Exemple :</strong> Vous vous versez 80 000$/an en dividendes → entrez <strong>80 000</strong>
                      </div>
                    </div>
                  </div>
                  <Input type="number" value={config.retraitement_dividendes} onChange={(e) => updateConfig('retraitement_dividendes', parseFloat(e.target.value) || 0)} placeholder="0" className="mt-1" />
                </div>

                {/* Salaire proprio */}
                <div className="p-4 rounded-lg border border-gray-200 bg-gray-50/50 space-y-2">
                  <div className="flex items-start gap-2">
                    <Plus className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <Label className="text-sm font-semibold">Excédent de salaire propriétaire</Label>
                      <p className="text-xs text-gray-500 mt-0.5">Si votre salaire dépasse ce qu&apos;un gestionnaire embauché coûterait, ajoutez la différence. Un acheteur remplacerait par un salaire normal.</p>
                      <div className="bg-white border border-dashed border-blue-200 rounded p-2 mt-2 text-xs text-blue-700">
                        <strong>Exemple :</strong> Votre salaire = 180 000$, salaire marché d&apos;un directeur = 120 000$ → excédent = <strong>60 000</strong>
                      </div>
                    </div>
                  </div>
                  <Input type="number" value={config.retraitement_salaire_proprio} onChange={(e) => updateConfig('retraitement_salaire_proprio', parseFloat(e.target.value) || 0)} placeholder="0" className="mt-1" />
                </div>

                {/* Loyer */}
                <div className="p-4 rounded-lg border border-gray-200 bg-gray-50/50 space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-bold text-blue-600 mt-0.5 shrink-0 w-4">±</span>
                    <div className="flex-1">
                      <Label className="text-sm font-semibold">Ajustement loyer (annuel)</Label>
                      <p className="text-xs text-gray-500 mt-0.5">Si vous payez votre local moins cher (ou plus cher) que le prix du marché, ajustez la différence. Entrez positif si sous le marché, négatif si au-dessus.</p>
                      <div className="bg-white border border-dashed border-blue-200 rounded p-2 mt-2 text-xs text-blue-700">
                        <strong>Exemple :</strong> Marché = 48 000$/an, vous payez 30 000$/an → économie de <strong>18 000</strong> (entrez +18 000)
                      </div>
                    </div>
                  </div>
                  <Input type="number" value={config.retraitement_loyer_marche} onChange={(e) => updateConfig('retraitement_loyer_marche', parseFloat(e.target.value) || 0)} placeholder="0 (positif ou négatif)" className="mt-1" />
                </div>

                {/* Dépenses exceptionnelles */}
                <div className="p-4 rounded-lg border border-gray-200 bg-gray-50/50 space-y-2">
                  <div className="flex items-start gap-2">
                    <Plus className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <Label className="text-sm font-semibold">Dépenses non-récurrentes à rajouter</Label>
                      <p className="text-xs text-gray-500 mt-0.5">Coûts exceptionnels qui n&apos;arriveront pas l&apos;année prochaine. On les rajoute car ils ne représentent pas la vraie charge d&apos;opération.</p>
                      <div className="bg-white border border-dashed border-blue-200 rounded p-2 mt-2 text-xs text-blue-700">
                        <strong>Exemples :</strong> Frais légaux d&apos;une poursuite (25 000$), réparation majeure exceptionnelle, déménagement de bureau
                      </div>
                    </div>
                  </div>
                  <Input type="number" value={config.retraitement_one_time_neg} onChange={(e) => updateConfig('retraitement_one_time_neg', parseFloat(e.target.value) || 0)} placeholder="0" className="mt-1" />
                </div>

                {/* Revenus exceptionnels */}
                <div className="p-4 rounded-lg border border-gray-200 bg-gray-50/50 space-y-2">
                  <div className="flex items-start gap-2">
                    <Minus className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <Label className="text-sm font-semibold">Revenus non-récurrents à enlever</Label>
                      <p className="text-xs text-gray-500 mt-0.5">Revenus exceptionnels qui ne se répéteront pas. On les enlève pour ne pas gonfler artificiellement la valeur.</p>
                      <div className="bg-white border border-dashed border-red-100 rounded p-2 mt-2 text-xs text-red-700">
                        <strong>Exemples :</strong> Remboursement d&apos;assurance (15 000$), vente d&apos;un véhicule, subvention gouvernementale one-time
                      </div>
                    </div>
                  </div>
                  <Input type="number" value={config.retraitement_one_time_pos} onChange={(e) => updateConfig('retraitement_one_time_pos', parseFloat(e.target.value) || 0)} placeholder="0" className="mt-1" />
                </div>
              </div>

              <div className="bg-blue-600 rounded-lg p-4 flex justify-between items-center text-white">
                <div>
                  <p className="font-bold text-lg">EBITDA Ajusté</p>
                  <p className="text-blue-200 text-sm">
                    {ebitdaBase.toFixed(0)}$ base {totalRetraitements >= 0 ? '+' : ''}{totalRetraitements.toFixed(0)}$ retraitements
                  </p>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(adjustedEbitda)}</p>
              </div>
            </CardContent>
          </Card>

          {/* SECTION 3 : Multiple */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center">3</span>
                Le Multiple d&apos;EBITDA — Prix que paie un acheteur
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <InfoBox title="Qu'est-ce que le « multiple » ? Comment le choisir ?" icon={HelpCircle} color="purple" defaultOpen={true}>
                <p>Le <strong>multiple</strong> représente le nombre d&apos;années de profit qu&apos;un acheteur est prêt à payer. Un multiple de <strong>4×</strong> signifie qu&apos;un acheteur paie <em>4 ans de profit à l&apos;avance</em>.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  <div className="bg-white rounded p-2 border border-purple-200 text-xs">
                    <strong className="text-purple-700">Multiple BAS (2-3×) :</strong> L&apos;entreprise dépend trop du propriétaire. Sans vous, les clients partent. Risqué pour un acheteur.
                  </div>
                  <div className="bg-white rounded p-2 border border-purple-200 text-xs">
                    <strong className="text-purple-700">Multiple ÉLEVÉ (4-6×) :</strong> Systèmes en place, équipe autonome, clients fidélisés. Un acheteur peut reprendre sans tout reconstruire.
                  </div>
                </div>
                <p className="mt-2 text-xs bg-white rounded p-2 border border-purple-200">
                  <strong>Votre cas :</strong> Avec {formatCurrency(donneesAnnuelles?.revenus || 0)} de CA et une équipe structurée, un multiple de <strong>3x à 4x</strong> est réaliste. Utilisez l&apos;onglet &quot;Guide&quot; pour affiner.
                </p>
              </InfoBox>

              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm text-gray-500 mb-2">
                      <span>1× (risqué)</span>
                      <span className="font-bold text-xl text-purple-700">{multipleActuel}×</span>
                      <span>8× (exceptionnel)</span>
                    </div>
                    <Slider
                      min={1} max={8} step={0.25}
                      value={[multipleActuel]}
                      onValueChange={([v]) => updateConfig('multiple', v)}
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>Solo / dépendant</span>
                      <span>PME solide</span>
                      <span>Leader régional</span>
                    </div>
                  </div>
                </div>

                {/* Formule visuelle */}
                <div className="flex items-center justify-center gap-2 sm:gap-4 py-4 bg-gray-50 rounded-lg flex-wrap">
                  <div className="text-center">
                    <p className="text-xs text-blue-600 font-medium">EBITDA Ajusté</p>
                    <p className="text-lg sm:text-xl font-bold text-blue-800">{formatCurrency(adjustedEbitda)}</p>
                  </div>
                  <span className="text-2xl text-gray-400 font-light">×</span>
                  <div className="text-center">
                    <p className="text-xs text-purple-600 font-medium">Multiple</p>
                    <p className="text-lg sm:text-xl font-bold text-purple-800">{multipleActuel}×</p>
                  </div>
                  <span className="text-2xl text-gray-400 font-light">=</span>
                  <div className="text-center">
                    <p className="text-xs text-emerald-600 font-medium">Valeur d&apos;entreprise</p>
                    <p className="text-lg sm:text-xl font-bold text-emerald-800">{formatCurrency(valeurEntreprise)}</p>
                  </div>
                </div>

                {/* Fourchette */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" /> Fourchette de valorisation selon le scénario
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: "Scénario pessimiste", mult: Math.max(1, multipleActuel - 1), color: "red", desc: "Vendez en urgence, marché difficile" },
                      { label: "Scénario réaliste", mult: multipleActuel, color: "emerald", desc: "Conditions normales de marché" },
                      { label: "Scénario optimiste", mult: multipleActuel + 1, color: "blue", desc: "Bon acheteur stratégique" },
                    ].map(({ label, mult, color, desc }) => (
                      <div key={label} className={`p-3 bg-${color}-50 border border-${color}-200 rounded-lg text-center`}>
                        <p className={`text-xs text-${color}-600 font-medium`}>{label}</p>
                        <p className={`text-base font-bold text-${color}-800`}>{mult}×</p>
                        <p className={`text-sm font-bold text-${color}-700`}>{formatCurrency(adjustedEbitda * mult)}</p>
                        <p className={`text-xs text-${color}-500 mt-1`}>{desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SECTION 4 : Résultat final */}
          <Card className="border-2 border-amber-300 bg-amber-50/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">4</span>
                Résultat Final — Ce que vous empochez
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoBox title="Valeur d'entreprise ≠ argent dans vos poches. Voici pourquoi." icon={HelpCircle} color="amber">
                <p>La <strong>valeur d&apos;entreprise</strong> ({formatCurrency(valeurEntreprise)}) est le prix total d&apos;achat. Mais un acheteur paie avec cet argent <strong>VOS dettes actuelles</strong> en premier ({formatCurrency(totalDettes)}). Ce qui reste va dans votre poche.</p>
                <p className="mt-2 bg-white rounded p-2 border border-amber-200 text-xs">
                  C&apos;est comme vendre une maison valant 500 000$ avec une hypothèque de 200 000$. Vous empochez <strong>300 000$</strong>, pas 500 000$.
                </p>
              </InfoBox>
              <div className="mt-4 space-y-2">
                <div className="flex justify-between py-2 border-b text-sm">
                  <span className="text-gray-600">Prix de vente (valeur d&apos;entreprise)</span>
                  <span className="font-bold text-purple-700">{formatCurrency(valeurEntreprise)}</span>
                </div>
                <div className="flex justify-between py-2 border-b text-sm">
                  <span className="text-gray-600">− Remboursement de vos dettes actuelles</span>
                  <span className="font-bold text-red-600">− {formatCurrency(totalDettes)}</span>
                </div>
                <div className="flex justify-between py-3 text-xl font-bold bg-white rounded-lg px-3 border border-amber-200">
                  <span className="text-gray-800">= Votre chèque final</span>
                  <span className={valeurCapitauxPropres >= 0 ? 'text-amber-700' : 'text-red-700'}>{formatCurrency(valeurCapitauxPropres)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ════════════════════════════════════
            ONGLET BILAN
        ════════════════════════════════════ */}
        <TabsContent value="bilan" className="space-y-4 mt-4">
          <InfoBox title="À quoi sert l'onglet Bilan ?" icon={HelpCircle} color="gray" defaultOpen={true}>
            <p>Cet onglet montre les <strong>actifs physiques</strong> de l&apos;entreprise (véhicules, équipements…) et vos <strong>dettes</strong> actuelles. Ces données proviennent directement de votre page <em>Actifs & Dettes</em>.</p>
            <p className="mt-2">Dans le calcul de valorisation, les dettes sont <strong>soustraites</strong> de la valeur d&apos;entreprise pour obtenir ce que vous empochez réellement à la vente.</p>
          </InfoBox>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-emerald-600" />
                  Actifs ({actifsDettes.assets.length}) — Valeur actuelle après dépréciation
                </CardTitle>
              </CardHeader>
              <CardContent>
                {actifsDettes.assets.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Aucun actif. Allez dans <a href="/actifs-dettes" className="text-emerald-600 underline">Actifs & Dettes</a> pour les ajouter.</p>
                ) : (
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {actifsDettes.assets.map((a, i) => {
                      const purchaseDate = new Date(a.purchaseDate);
                      const monthsElapsed = (now.getFullYear() - purchaseDate.getFullYear()) * 12 + (now.getMonth() - purchaseDate.getMonth());
                      const purchaseValue = parseFloat(a.purchaseValue) || 0;
                      const residualValue = parseFloat(a.residualValue) || 0;
                      const years = parseInt(a.depreciationYears) || 5;
                      const monthlyDep = (purchaseValue - residualValue) / (years * 12);
                      const currentValue = Math.max(residualValue, purchaseValue - (monthlyDep * Math.max(0, monthsElapsed)));
                      return (
                        <div key={i} className="flex justify-between items-center py-1.5 border-b last:border-0 text-sm">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{a.name}</p>
                            <p className="text-xs text-gray-400">{a.type} · acheté {formatCurrency(purchaseValue)}</p>
                          </div>
                          <p className="font-semibold text-emerald-700 ml-2 shrink-0">{formatCurrency(currentValue)}</p>
                        </div>
                      );
                    })}
                    <div className="flex justify-between pt-2 font-bold border-t-2 border-emerald-300">
                      <span>Total actifs actuels</span>
                      <span className="text-emerald-700">{formatCurrency(totalActifs)}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-red-600" />
                  Dettes ({actifsDettes.debts.length}) — Soldes actuels à rembourser
                </CardTitle>
              </CardHeader>
              <CardContent>
                {actifsDettes.debts.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Aucune dette enregistrée.</p>
                ) : (
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {actifsDettes.debts.map((d, i) => (
                      <div key={d.id || d._id || i} className="flex justify-between items-center py-1.5 border-b last:border-0 text-sm">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{d.name}</p>
                          <p className="text-xs text-gray-400">{d.type} · {d.interestRate}% · {formatCurrency(parseFloat(d.monthlyPayment))}/mois</p>
                        </div>
                        <p className="font-semibold text-red-600 ml-2 shrink-0">{formatCurrency(parseFloat(d.currentBalance))}</p>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 font-bold border-t-2 border-red-300">
                      <span>Total dettes actuelles</span>
                      <span className="text-red-600">{formatCurrency(totalDettes)}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="border-2 border-amber-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Synthèse finale — Votre chèque de vente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between py-2 border-b text-sm">
                <span className="text-gray-600 flex items-center gap-1">
                  Valeur d&apos;entreprise
                  <Tooltip text="Ce qu'un acheteur paie pour acquérir votre entreprise complète, dettes incluses." />
                </span>
                <span className="font-bold text-purple-700">{formatCurrency(valeurEntreprise)}</span>
              </div>
              <div className="flex justify-between py-2 border-b text-sm">
                <span className="text-gray-600 flex items-center gap-1">
                  − Dettes à rembourser lors de la vente
                  <Tooltip text="L'acheteur reprend vos dettes ou les remboursez avec le produit de la vente." />
                </span>
                <span className="font-bold text-red-600">− {formatCurrency(totalDettes)}</span>
              </div>
              <div className="flex justify-between py-4 font-bold text-lg bg-amber-50 rounded-lg px-3 border border-amber-200">
                <span>= Ce que vous empochez</span>
                <span className={valeurCapitauxPropres >= 0 ? 'text-amber-700' : 'text-red-700'}>{formatCurrency(valeurCapitauxPropres)}</span>
              </div>
              {valeurCapitauxPropres < 0 && (
                <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <p>Valeur négative : vos dettes dépassent la valeur d&apos;entreprise avec le multiple actuel. Essayez un multiple plus élevé ou réduisez vos dettes avant de vendre.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ════════════════════════════════════
            ONGLET GUIDE
        ════════════════════════════════════ */}
        <TabsContent value="guide" className="space-y-4 mt-4">
          <InfoBox title="Comment utiliser ce guide ?" icon={BookOpen} color="blue" defaultOpen={true}>
            <p>Cliquez sur la ligne qui correspond le mieux à votre situation. Le multiple sera automatiquement appliqué à votre calcul. <strong>Soyez honnête dans votre évaluation</strong> — surestimer le multiple lors d&apos;une vraie vente mène à des négociations difficiles.</p>
          </InfoBox>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Échelle des multiples — Secteur des Services au Québec</CardTitle>
              <CardDescription>Cliquez sur une ligne pour appliquer ce multiple à votre calcul</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {MULTIPLES_GUIDE.map((item, i) => {
                  const [low, high] = item.range.split(' – ').map(parseFloat);
                  const isActive = multipleActuel >= low && multipleActuel <= high;
                  const midVal = (low + high) / 2;
                  return (
                    <div
                      key={i}
                      onClick={() => updateConfig('multiple', Math.round(midVal * 4) / 4)}
                      className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-all ${isActive ? 'bg-emerald-50 border-emerald-400 shadow-sm' : 'bg-gray-50 border-gray-200 hover:border-emerald-200 hover:bg-emerald-50/30'}`}
                    >
                      <Badge className={`shrink-0 text-sm font-bold ${isActive ? 'bg-emerald-600 text-white' : 'bg-purple-100 text-purple-800'}`}>{item.range}</Badge>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm">{item.label}</p>
                        <p className="text-sm text-gray-600 mt-0.5">{item.description}</p>
                        <p className="text-xs text-gray-400 italic mt-1">{item.exemple}</p>
                        {isActive && (
                          <p className="text-xs text-emerald-700 font-semibold mt-1">✓ Multiple actuellement sélectionné ({multipleActuel}×)</p>
                        )}
                      </div>
                      <div className="text-right shrink-0 hidden sm:block">
                        <p className="text-xs text-gray-400">Valeur estimée</p>
                        <p className={`text-sm font-bold ${isActive ? 'text-emerald-700' : 'text-gray-600'}`}>{formatCurrency(adjustedEbitda * midVal)}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-1" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-emerald-800 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" /> Ce qui augmente votre multiple
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-emerald-800 space-y-2">
                  {[
                    ["Revenus récurrents", "Clients annuels, contrats d'entretien → revenus prévisibles"],
                    ["Équipe autonome", "Chefs d'équipe qui fonctionnent sans vous au quotidien"],
                    ["Clientèle diversifiée", "Aucun client ne représente plus de 20% du CA"],
                    ["Systèmes documentés", "Procédures écrites, logiciels de gestion (comme Gestion Minute!)"],
                    ["Croissance régulière", "10%+ de croissance par an sur 3 ans consécutifs"],
                    ["Marque locale forte", "Réputation Google 4.5+ étoiles, présence réseaux sociaux"],
                    ["Barrières à l'entrée", "Équipements spécialisés (nacelle), certifications, territoire"],
                  ].map(([titre, desc]) => (
                    <li key={titre} className="flex items-start gap-2">
                      <span className="text-emerald-500 font-bold mt-0.5">+</span>
                      <div><strong>{titre}</strong> — <span className="text-emerald-700">{desc}</span></div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-red-200 bg-red-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-red-800 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Ce qui diminue votre multiple
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-red-800 space-y-2">
                  {[
                    ["Dépendance au propriétaire", "Si vous partez, les clients partent avec vous"],
                    ["Concentration client", "Un seul gros client représente 50%+ du CA"],
                    ["Personnel instable", "Fort roulement d'employés, pas de chefs d'équipe clés"],
                    ["Pas de systèmes", "Tout est dans la tête du propriétaire, rien de documenté"],
                    ["Revenus en déclin", "Baisse du CA 2+ années de suite"],
                    ["Dettes élevées", "Ratio endettement > 25% des revenus mensuels"],
                  ].map(([titre, desc]) => (
                    <li key={titre} className="flex items-start gap-2">
                      <span className="text-red-500 font-bold mt-0.5">−</span>
                      <div><strong>{titre}</strong> — <span className="text-red-700">{desc}</span></div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <Card className="border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-blue-800 flex items-center gap-2">
                <Info className="w-4 h-4" /> Note importante sur cette valorisation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-blue-800">
                Cette estimation est un <strong>outil de référence interne</strong>. Une valorisation officielle pour une vente réelle nécessite l&apos;intervention d&apos;un <strong>courtier en entreprises</strong> ou d&apos;un <strong>évaluateur agréé</strong> qui prendra également en compte la liste de clients, les contrats en cours, l&apos;achalandage (goodwill), et les conditions du marché local.
              </p>
              <p className="text-sm text-blue-700 mt-2">
                Cet outil est parfait pour <strong>suivre l&apos;évolution de votre valeur dans le temps</strong> et identifier les leviers pour augmenter votre multiple avant une éventuelle vente.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Revente;
